import { FunctionComponent, useState, useEffect } from 'react';
import {
  Navigate,
  useSearchParams,
  createSearchParams,
} from 'react-router-dom';
import { useNavigate } from 'react-router';

import { FormProvider, useForm } from 'react-hook-form';
import { signUp, isAuthenticated } from '../utils/auth';
import { acceptInvite } from '../utils/invite';
import { fetchApi } from '../utils/fetchApi';
import { ApiStatus } from '../utils/types';
import { Referral, ValidationErrorDetail } from '../types';
import {
  AlertVariants,
  BlockAlert,
  Button,
  FilledLockIcon,
  TextField,
} from '../components';

import logo from '../assets/logo.svg';

type SignUpIFormValues = {
  email: string;
  password: string;
  companyName: string;
};

export const SignUp: FunctionComponent = () => {
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan');
  const inviteToken = searchParams.get('invite');
  const referralCode = searchParams.get('code');
  const email = searchParams.get('email');
  const companyName = searchParams.get('company_name');

  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [invitedToCompanyName, setInvitedToCompanyName] = useState<string>('');
  const [referral, setReferral] = useState<Referral | null>(null);
  const [askForInviteEmail, setAskForInviteEmail] = useState<boolean>(false);

  const formMethods = useForm<SignUpIFormValues>({
    defaultValues: {
      email: email || '',
      companyName: companyName || '',
    },
  });
  const { handleSubmit } = formMethods;

  useEffect(() => {
    if (inviteToken) {
      fetchApi({
        url: `/api/invites/verify`,
        method: 'POST',
        body: { token: inviteToken },
      }).then(({ status, data }) => {
        if (status === ApiStatus.Error) {
          setError('This invite expired or is no longer valid');
          setDisableSubmit(true);
          return;
        }
        setInvitedToCompanyName(data.legal_entity_name);
        setAskForInviteEmail(data.ask_for_email);
      });
    }

    if (referralCode) {
      fetchApi({
        url: `/api/referrals/verify`,
        method: 'POST',
        body: { code: referralCode },
      }).then(({ status, data }) => {
        if (status === ApiStatus.Error) {
          setError('This referral link expired or is no longer valid');
          setDisableSubmit(true);
          return;
        }
        setReferral(data);
      });
    }

    // Add a dependency on the invite token so we don't accidentally
    // call the `/verify` endpoint every time this component is
    // re-rendered
  }, [inviteToken, referralCode]);

  const onSubmit = async (data: SignUpIFormValues) => {
    const { companyName, email, password } = data;

    setDisableSubmit(true);
    // Clear out existing errors
    setError('');
    try {
      if (inviteToken) {
        await acceptInvite(inviteToken, password);
      } else if (referralCode) {
        await signUp(companyName, email, password, referralCode);
      } else {
        await signUp(companyName, email, password, null);
      }

      // Only create url search param if selectedPlan is truthy
      const selectedPlanParam =
        selectedPlan &&
        createSearchParams({
          plan: selectedPlan,
        });

      const redirectUrl = selectedPlanParam
        ? `/home?${selectedPlanParam.toString()}`
        : '/home';

      navigate(redirectUrl);
    } catch (err: any) {
      if (err instanceof Error) {
        // Handle errors thrown from frontend
        setError(err.message);
      } else if (err === 'Invalid username domain') {
        setError('Please ensure you provided a valid company email address');
      } else if (err === 'Account already exists') {
        setError('An account already exists for this email address.');
      } else {
        // Handle errors thrown from backend
        // Only show the first validation error from the API
        const valError = (err as ValidationErrorDetail)[0];
        if (valError.loc.indexOf('username') > -1) {
          setError('Please ensure you provided a valid company email address');
        } else if (valError.loc.indexOf('password') > -1) {
          setError('Please ensure your password has at least 11 characters');
        } else {
          setError('Please check the fields below to make sure they are valid');
        }
      }
    }
    setDisableSubmit(false);
  };

  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 pb-48 pt-12 sm:px-6 lg:px-8">
      <div className="mb-10 w-full max-w-md pb-4">
        <div className="w-20">
          <img src={logo} alt="Logo" />
        </div>
      </div>
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-700">
            Create your account
            {invitedToCompanyName && ` and join ${invitedToCompanyName}`}
          </h2>
          {referral && (
            <p className="mt-2 text-gray-600">
              {referral.partner_display_name && (
                <>
                  {referral.partner_display_name} and Mosey have joined forces
                  to help you manage employment and tax compliance.
                </>
              )}
              {referral.discount_percent && referral.discount_percent > 0 && (
                <span className="font-semibold text-lime-500">
                  {' '}
                  You save {referral.discount_percent}%
                  <span className="font-normal text-gray-600">.</span>
                </span>
              )}
            </p>
          )}
          {!referral && (
            <p className="mt-2 text-gray-600">
              <span className="mr-1">Or</span>
              <a
                href="/login"
                className="font-medium text-teal-600 hover:text-teal-700"
              >
                log in
              </a>
            </p>
          )}
        </div>
        <FormProvider {...formMethods}>
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <BlockAlert variant={AlertVariants.Error}>{error}</BlockAlert>
            )}
            <input type="hidden" name="remember" value="true" />
            <div className="-space-y-px rounded-md shadow-sm">
              {!inviteToken && (
                <TextField
                  name="companyName"
                  inputClassName="relative focus:z-10 rounded-b-none"
                  hasMargin={false}
                  autoComplete="text"
                  placeholder="Company name"
                />
              )}
              {(!inviteToken || askForInviteEmail) && (
                <TextField
                  name="email"
                  inputClassName="relative focus:z-10 rounded-t-none rounded-b-none"
                  type="email"
                  autoComplete="email"
                  hasMargin={false}
                  placeholder="Email address"
                />
              )}
              <TextField
                name="password"
                inputClassName={`relative focus:z-10 ${
                  (!inviteToken || askForInviteEmail) && 'rounded-t-none'
                }`}
                type="password"
                autoComplete="current-password"
                hasMargin={false}
                placeholder="Set a password"
              />
            </div>

            <div>
              <Button
                type="submit"
                size="large"
                leftIcon={<FilledLockIcon className="size-5" />}
                isFullWidth
                isLoading={disableSubmit}
                disabled={disableSubmit}
              >
                Submit
              </Button>
              <div className="mt-4 text-sm text-gray-500">
                By creating an account, you are agreeing to the Mosey{' '}
                <a
                  href="https://mosey.com/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-teal-600 hover:text-teal-700"
                >
                  terms of service
                </a>{' '}
                and{' '}
                <a
                  href="https://mosey.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-teal-600 hover:text-teal-700"
                >
                  privacy policy
                </a>
                .
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default SignUp;
